import React, { useEffect, useState } from 'react';
import './view-controls.scss';
import {
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  DeleteIcon,
  GridViewIcon,
  ListViewIcon,
  ReloadIcon,
  DownloadIcon
} from '@components/icons/svg-icon.tsx';
import {
  ViewControlsEnum,
  ViewControlsProps
} from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import { useTranslation } from 'react-i18next';
import useLanguageStore from '@store/language-store/language.store';

const ViewControls: React.FC<ViewControlsProps> = ({
  onAction,
  selectedRows,
  showImport = false,
  showLanguageSelect,
  showReload = false,
  isReloading = false,
  showDelete = true,
  showRelease = false,
  extraButtons
}) => {
  const { languageList } = useLanguageStore((state) => state);
  const storedLang = localStorage.getItem('language');

  const [selectedLanguage, setSelectedLanguage] = useState(
    storedLang ? storedLang : languageList[0]?.value || LanguageEnum.English
  );
  const [view, setView] = useState(ViewControlsEnum.List);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    onAction({ type: ViewControlsEnum.Language, value: selectedLanguage });
    onAction({ type: ViewControlsEnum.View, value: view });
    onAction({ type: ViewControlsEnum.Search, value: searchTerm });
    onAction({ type: ViewControlsEnum.SelectAll, value: selectAll });
  }, []);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const value = event.target?.value;
    setSelectedLanguage(value as LanguageEnum);
    onAction({ type: ViewControlsEnum.Language, value });
    localStorage.setItem('language', value);
  };

  const handleViewChange = (newView: string) => {
    setView(newView as ViewControlsEnum);
    onAction({ type: ViewControlsEnum.View, value: newView });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    onAction({ type: ViewControlsEnum.Search, value });
  };

  const handleDelete = () => onAction({ type: ViewControlsEnum.Delete });

  const handleAttachNewRelease = () =>
    onAction({ type: ViewControlsEnum.AttachNewRelease });

  const handleReload = () => onAction({ type: ViewControlsEnum.Reload });

  const handleImport = () =>
    onAction({ type: ViewControlsEnum.ImportPlaylist });

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    onAction({ type: ViewControlsEnum.SelectAll, value: checked });
  };

  return (
    <div className="flex justify-between items-center min-h-8.5">
      <div>
        {/* <FormControlLabel
          className="!ml-0"
          control={
            <Checkbox checked={selectAll} onChange={handleSelectAllChange} />
          }
          label={
            <span className="text-(--blue-4) ml-2">
              {t('view_controls_select_all')}
            </span>
          }
        /> */}
      </div>

      <div className="flex items-center gap-2">
        {/*<TextField*/}
        {/*  variant="outlined"*/}
        {/*  placeholder="Search"*/}
        {/*  value={searchTerm}*/}
        {/*  onChange={handleSearchChange}*/}
        {/*  size="small"*/}
        {/*/>*/}

        {extraButtons}

        {showReload && selectedRows && selectedRows.length > 0 && (
          <Button
            className="h-8.5 min-w-25! px-3! text-[12px] border-(--gray-6)! text-[rgba(25,27,27,0.6)]! bg-(--gray-11)!"
            variant="outlined"
            onClick={handleReload}
            // startIcon={<ReloadIcon />}
            startIcon={
              isReloading ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <ReloadIcon />
              )
            }
          >
            {t('view_controls_reload')}
          </Button>
        )}

        {showLanguageSelect !== false && (
          <FormControl sx={{ minWidth: 170 }}>
            <Select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              displayEmpty
              renderValue={(value) => {
                const language = languageList.find(
                  (lang) => lang.value === value
                );
                return (
                  <div className="flex items-center text-[12px]">
                    <img
                      className="h-5 w-5 mr-2 rounded-4xl object-cover"
                      src={language?.flag}
                      alt={language?.label}
                    />
                    {language?.label || 'Unknown'}
                  </div>
                );
              }}
              sx={{
                fontSize: 12,
                height: 34,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '17px'
                }
              }}
            >
              {languageList.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={lang.flag}
                      alt={lang.label}
                      style={{
                        width: 20,
                        height: 20,
                        marginRight: 8,
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                    {lang.label}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showRelease && selectedRows && selectedRows.length > 0 && (
          <Button
            className="h-8.5 min-w-25! px-5! text-[12px] text-[rgba(25,27,27,0.60)]! bg-(--gray-11)! border-(--gray-6)!"
            variant="outlined"
            startIcon={<DownloadIcon stroke="var(--blue-4)" />}
            onClick={handleAttachNewRelease}
          >
            {t('view_controls_attach_new_release')}
          </Button>
        )}

        {showImport && (
          <Button
            className="h-8.5 min-w-25! px-5! text-[12px] text-[rgba(25,27,27,0.60)]! bg-(--gray-11)! border-(--gray-6)!"
            variant="outlined"
            startIcon={<DownloadIcon stroke="var(--blue-4)" />}
            onClick={handleImport}
          >
            {t('view_controls_import_playlist')}
          </Button>
        )}

        {showDelete && selectedRows && selectedRows.length > 0 && (
          <Button
            className="h-8.5 min-w-25! px-3! text-[12px] border-(--red-3)! text-(--red-3)!"
            variant="outlined"
            onClick={handleDelete}
            startIcon={<DeleteIcon />}
          >
            {t('view_controls_delete')}
          </Button>
        )}

        <div className="flex gap-4">
          {/* <Button
            sx={{
              height: 34,
              paddingLeft: '20px',
              paddingRight: '20px',
              minWidth: '100px !important',
              borderColor: 'var(--gray-6)',
              backgroundColor: 'var(--gray-11)',
              color: 'rgba(25, 27, 27, 0.60)',
              fontSize: 12
            }}
            className={`${view === ViewControlsEnum.Grid ? 'active-button' : ''}`}
            variant="outlined"
            onClick={() => handleViewChange(ViewControlsEnum.Grid)}
            startIcon={<GridViewIcon />}
          >
            {t('view_controls_grid_view')}
          </Button> */}
          {/* <Button
            sx={{
              height: 34,
              paddingLeft: '20px',
              paddingRight: '20px',
              minWidth: '100px !important',
              borderColor: 'var(--gray-6)',
              backgroundColor: 'var(--gray-11)',
              color: 'rgba(25, 27, 27, 0.60)',
              fontSize: 12
            }}
            className={`${view === ViewControlsEnum.List ? 'active-button' : ''}`}
            variant="outlined"
            onClick={() => handleViewChange(ViewControlsEnum.List)}
            startIcon={<ListViewIcon />}
          >
            {t('view_controls_list_view')}
          </Button> */}
        </div>
      </div>
    </div>
  );
};

export default ViewControls;
